// Premium Personal Branding Content for Mona Bizzari
// Luxury Healing Sanctuary - Heba Style

export const content = {
  en: {
    // Brand Identity
    brand: {
      name: "Mona Bizzari",
      tagline: "Life Fixer | Quality of Life Therapist",
      shortDescription:
        "A trusted digital sanctuary supporting people on their healing journey, offering guided programs, workshops, and immersive experiences that nurture emotional well-being, personal growth, and lasting balance.",
    },

    // Navigation - Heba Style
    nav: {
      mission: "Mission",
      healing: "Healing",
      programs: "Programs",
      challenges: "Challenges",
      about: "About",
      testimonials: "Testimonials",
      gallery: "Gallery",
      connect: "Connect",
      languageToggle: "العربية",
    },

    // Hero Section
    hero: {
      headline:
        "A calm, trusted space for women to heal, grow, and reconnect with themselves",
      subtext:
        "Guided by a Positive Psychologist, Emotional Intelligence Specialist, and Certified Crystal Healer — helping women find clarity, peace, and a gentler way forward.",
      cta: "Join OUR Free Healing Community",
      statement: "Life Fixer | Quality of Life Therapist",
      description:
        "A calm, trusted space supporting people on their healing journey.",
    },

    // Mission Section
    mission: {
      tagline: "The Journey",
      headline: "You don't have to heal alone",
      content: [
        "This is a digital sanctuary created for those seeking clarity, balance, and emotional healing.",
        "Here, healing is gentle, intentional, and deeply personal.",
        "Through guided programs, live events, and structured learning experiences, we support you wherever you are, and walk alongside you as you move forward.",
      ],
      cta: "Begin Your Journey",
    },

    // Paths to Healing
    paths: {
      tagline: "Modalities",
      headline: "Paths to Healing",
      description:
        "Each path offers a unique doorway to balance, clarity, and inner peace.",
      items: [
        {
          id: "crystal-healing",
          title: "Crystal Healing",
          description:
            "A gentle energetic practice supporting balance, grounding, and inner alignment.",
          icon: "crystal",
        },
        {
          id: "emotional-intelligence",
          title: "Emotional Intelligence",
          description:
            "Evidence-informed approaches to understanding and managing emotions.",
          icon: "heart",
        },
        {
          id: "positive-psychology",
          title: "Positive Psychology",
          description:
            "Focusing on strengths, resilience, and sustainable well-being.",
          icon: "sun",
        },
        {
          id: "salt-therapy",
          title: "Salt Therapy",
          description:
            "Halotherapy experiences for respiratory wellness and deep relaxation.",
          icon: "wind",
        },
      ],
    },

    // Crystal Healing Session - Featured Service
    crystalHealing: {
      tagline: "Featured Experience",
      headline: "Crystal Healing Session",
      intro:
        "A gentle, deeply grounding experience designed to restore balance and harmony to your energy system in a calm and supportive way.",
      content: [
        "During the session, carefully selected natural-colored crystals are placed on nine key energy points across the body. Each placement is intentional, supporting the body's natural flow of energy and encouraging a sense of inner alignment, clarity, and emotional ease.",
        "Many people carry emotional stress and energetic tension long before it manifests physically. As this energetic heaviness begins to release, clients often describe feeling lighter, more open, and emotionally balanced. Some also notice a softening of physical tension and a greater sense of comfort and clarity.",
        "The session creates a quiet, safe space that allows the nervous system to slow down and the energy to gently recalibrate. Most clients report a noticeable shift in their emotional and energetic state immediately after the session, with effects that continue to unfold over time.",
        "This experience is designed to complement personal growth and emotional well-being, offering a nurturing way to reconnect with yourself without pressure or urgency.",
      ],
      benefits: [
        "Restore energetic balance",
        "Release emotional tension",
        "Feel lighter and more open",
        "Soften physical tension",
        "Gain clarity and comfort",
        "Recalibrate your nervous system",
      ],
      cta: "Book Your Session",
    },

    // About Mona
    about: {
      tagline: "Meet Your Guide",
      headline: "Mona Bizzari",
      role: "Positive Psychologist · Emotional Intelligence Specialist · Certified Crystal Healer",
      story: [
        "Mona Bizzari is a positive psychologist, emotional intelligence specialist, and certified crystal healer. She is the owner of a healing center and salt cave and holds a Computer Science degree from the American University in Cairo.",
        "Her work blends science, empathy, and energy-based healing into a multidimensional approach to emotional wellness. Mona creates spaces where clients feel safe, understood, and deeply supported.",
        "Through one-on-one sessions, workshops, and retreats, she helps people develop emotional intelligence, resilience, and lasting inner balance.",
      ],
      credentials: [
        "Positive Psychology Practitioner",
        "Emotional Intelligence Specialist",
        "Certified Crystal Healer",
        "Founder, Healing Center & Salt Cave",
        "Computer Science, American University in Cairo",
      ],
      cta: "Work With Mona",
    },

    // Vision Section
    vision: {
      tagline: "Philosophy",
      headline: "Healing as a Way of Living",
      content: [
        "Healing is not a moment—it is a way of living.",
        "Transformation happens through awareness, consistency, compassion, and community.",
        "This platform exists to support healing as a sustainable lifestyle.",
      ],
      principles: [
        {
          title: "Awareness",
          description: "Understanding ourselves deeply and honestly",
        },
        {
          title: "Consistency",
          description: "Small, intentional steps that compound over time",
        },
        {
          title: "Compassion",
          description: "Gentle patience with ourselves and others",
        },
        {
          title: "Community",
          description: "Healing together in safe, supportive spaces",
        },
      ],
    },

    // Programs Section
    programs: {
      tagline: "Programs & Experiences",
      headline: "Your Healing Journey",
      description:
        "Thoughtfully designed programs to meet you where you are and guide you toward lasting transformation.",
      items: [
        {
          id: "healthy-tuesday",
          title: "Healthy Tuesday",
          arabicTitle: "الثلاثاء الصحي",
          subtitle: "Weekly Healing Community",
          badge: "Free",
          description:
            "A weekly in-person healing community focused on emotional awareness, stress release, energy balance, and meaningful connection.",
          format: "In-Person · Weekly",
          focus: [
            "Emotional awareness",
            "Stress release",
            "Energy balance",
            "Learning & connection",
          ],
          outcomes: [
            "Calm and clarity",
            "Social confidence",
            "Sense of belonging",
            "Emotional resilience",
          ],
          cta: "Join Our FREE community",
        },
        {
          id: "ensan-farid",
          title: "Ensan Farid",
          arabicTitle: "إنسان فريد",
          subtitle: "A Unique Being",
          badge: "3-Month Journey",
          description:
            "A transformative 3-month identity and emotional mastery journey designed to help you reconnect with your authentic self.",
          format: "Intensive Program · 3 Months",
          focus: [
            "Self-worth & identity",
            "Healthy boundaries",
            "Emotional clarity",
            "Authentic living",
          ],
          outcomes: [
            "Unshakeable confidence",
            "Emotional independence",
            "Clear personal boundaries",
            "Authentic self-expression",
          ],
          investment: {
            monthly: "EGP 3,000 per month",
            full: "EGP 9,000 — full program payment",
            prepaid: "EGP 8,000 — prepaid discount (pay in advance and save)",
          },
          cta: "Apply Now",
        },
        {
          id: "youm-el-mona",
          title: "Youm El Mona",
          arabicTitle: "يوم المنى",
          subtitle: "A Day of Wishes",
          badge: "Immersive Experience",
          description:
            "A one-day deep healing experience designed for emotional reset, energetic renewal, and conscious pause.",
          format: "Full Day Immersion",
          focus: [
            "Emotional reset",
            "Energetic renewal",
            "Conscious pause",
            "Deep reflection",
          ],
          outcomes: [
            "Complete energy renewal",
            "Emotional release",
            "Clarity and peace",
            "Renewed sense of purpose",
          ],
          investment: {
            base: "EGP 1,500 — one-day experience",
          },
          cta: "Reserve Your Spot",
        },
      ],
      consultation: {
        title: "Not sure where to start?",
        description:
          "Book a complimentary discovery call and let's find the perfect path for your healing journey.",
        cta: "Book Discovery Call",
      },
    },

    // Testimonials
    testimonials: {
      tagline: "Transformations",
      headline: "Stories of Healing",
      description: "Real experiences from women who have walked this path.",
      items: [
        {
          name: "Sarah A.",
          location: "Dubai, UAE",
          program: "Ensan Farid Graduate",
          quote:
            "Mona's program changed my life. I finally understand who I am and what I want. The emotional boundaries I've built have transformed my relationships and my peace of mind.",
          rating: 5,
        },
        {
          name: "Fatima K.",
          location: "Cairo, Egypt",
          program: "Healthy Tuesday Regular",
          quote:
            "Every Tuesday is now my anchor. The community Mona has built is incredible — supportive, non-judgmental, and genuinely caring. I've never felt so understood.",
          rating: 5,
        },
        {
          name: "Layla M.",
          location: "Riyadh, KSA",
          program: "Youm El Mona Participant",
          quote:
            "One day. That's all it took to shift something deep inside me. Mona creates such a safe space that I finally allowed myself to release years of held emotions.",
          rating: 5,
        },
        {
          name: "Nour H.",
          location: "Dubai, UAE",
          program: "Ensan Farid Graduate",
          quote:
            "I came to Mona feeling lost and disconnected. Three months later, I'm a completely different woman — confident, clear, and finally at peace with myself.",
          rating: 5,
        },
        {
          name: "Amira S.",
          location: "Alexandria, Egypt",
          program: "Crystal Healing Sessions",
          quote:
            "The combination of positive psychology and crystal healing is unique and powerful. Mona's approach is grounded in science but also deeply intuitive.",
          rating: 5,
        },
        {
          name: "Hana R.",
          location: "Dubai, UAE",
          program: "Multiple Programs",
          quote:
            "I've attended almost everything Mona offers. Each experience builds on the last. She truly understands the journey of healing and meets you exactly where you are.",
          rating: 5,
        },
      ],
    },

    // Gallery Section
    gallery: {
      tagline: "Moments",
      headline: "Healing in Action",
      description:
        "Glimpses from our programs, retreats, and community gatherings.",
    },

    // Connect Section
    connect: {
      tagline: "Let's Connect",
      headline: "Begin Your Journey",
      description:
        "Whether you have questions about a program, want to explore working together, or simply need guidance — I'm here.",
      email: "Mona@monabizzari.com",
      phone: "01006279021",
      workWith: {
        title: "Work With Mona",
        items: [
          "Programs & Courses",
          "Private Sessions",
          "Speaking Engagements",
          "Corporate Wellness",
          "Collaborations & Partnerships",
        ],
      },
      form: {
        name: "Your Name",
        email: "Email Address",
        subject: "Subject",
        subjectOptions: [
          "Programs & Courses",
          "Private Sessions",
          "Speaking & Events",
          "Collaborations",
          "General Inquiry",
        ],
        message: "Your Message",
        submit: "Send Message",
        success: "Thank you for reaching out. I'll respond within 48 hours.",
      },
    },

    // Footer
    footer: {
      tagline: "Life Fixer | Quality of Life Therapist",
      navigation: {
        title: "Navigate",
        items: ["Mission", "Programs", "About", "Testimonials", "Connect"],
      },
      programs: {
        title: "Programs",
        items: ["Healthy Tuesday", "Ensan Farid", "Youm El Mona"],
      },
      connect: {
        title: "Connect",
        items: ["Instagram", "Facebook"],
      },
      social: {
        instagram: "https://www.instagram.com/bizzarihealingcrystals",
        facebook: "https://www.facebook.com/mona.bizzari",
      },
      copyright: "© 2025 Mona Bizzari. All rights reserved.",
      madeWith: "Made with love by UVAII and Dr. Ahmad Zalat Team.",
    },
  },

  ar: {
    // Arabic Brand Identity
    brand: {
      name: "منى بزاري",
      tagline: "هنا للشفاء — العقل، الطاقة، ونمط الحياة",
      shortDescription:
        "ملاذ رقمي موثوق يدعم الأشخاص في رحلة شفائهم، يقدم برامج موجهة وورش عمل وتجارب غامرة تغذي الرفاهية العاطفية والنمو الشخصي والتوازن الدائم.",
    },

    // Navigation
    nav: {
      mission: "الرسالة",
      healing: "الشفاء",
      programs: "البرامج",
      challenges: "التحديات",
      about: "عني",
      testimonials: "قصص النجاح",
      gallery: "المعرض",
      connect: "تواصل",
      languageToggle: "English",
    },

    // Hero Section
    hero: {
      headline:
        "مساحة هادئة وموثوقة للنساء للشفاء والنمو وإعادة الاتصال بأنفسهن",
      subtext:
        "بتوجيه من متخصصة في علم النفس الإيجابي والذكاء العاطفي ومعالجة أحجار كريمة معتمدة — لمساعدة المرأة على إيجاد الوضوح والسلام وطريق أكثر لطفًا للأمام.",
      cta: "انضمي لمجتمعنا الان",
      statement: "هنا للشفاء — العقل، الطاقة، ونمط الحياة",
      description: "مساحة هادئة وموثوقة تدعم الأشخاص في رحلة شفائهم.",
    },

    // Mission Section
    mission: {
      tagline: "الرحلة",
      headline: "لا يجب أن تشفي وحدك",
      content: [
        "هذا ملاذ رقمي مُنشأ لمن يبحثون عن الوضوح والتوازن والشفاء العاطفي.",
        "هنا، الشفاء لطيف ومقصود وشخصي بعمق.",
        "من خلال البرامج الموجهة والفعاليات الحية وتجارب التعلم المنظمة، ندعمك أينما كنت، ونسير بجانبك وأنت تتقدم.",
      ],
      cta: "ابدأ رحلتك",
    },

    // Paths to Healing
    paths: {
      tagline: "الأساليب",
      headline: "مسارات الشفاء",
      description:
        "كل مسار يقدم بوابة فريدة نحو التوازن والوضوح والسلام الداخلي.",
      items: [
        {
          id: "crystal-healing",
          title: "شفاء الأحجار الكريمة",
          description:
            "ممارسة طاقية لطيفة تدعم التوازن والاتصال بالأرض والمواءمة الداخلية.",
          icon: "crystal",
        },
        {
          id: "emotional-intelligence",
          title: "الذكاء العاطفي",
          description: "مناهج مبنية على الأدلة لفهم وإدارة المشاعر.",
          icon: "heart",
        },
        {
          id: "positive-psychology",
          title: "علم النفس الإيجابي",
          description: "التركيز على نقاط القوة والمرونة والرفاهية المستدامة.",
          icon: "sun",
        },
        {
          id: "salt-therapy",
          title: "العلاج بالملح",
          description: "تجارب الهالوثيرابي للصحة التنفسية والاسترخاء العميق.",
          icon: "wind",
        },
      ],
    },

    // Crystal Healing Session - Featured Service
    crystalHealing: {
      tagline: "تجربة مميزة",
      headline: "جلسة الشفاء بالأحجار الكريمة العلاجية",
      intro:
        "تجربة لطيفة وعميقة الاتصال بالأرض مصممة لاستعادة التوازن والانسجام لنظام طاقتك بطريقة هادئة وداعمة.",
      content: [
        "خلال الجلسة، يتم وضع أحجار كريمة طبيعية مختارة بعناية على تسع نقاط طاقة رئيسية في الجسم. كل وضع مقصود، يدعم التدفق الطبيعي للطاقة في الجسم ويشجع على الشعور بالمواءمة الداخلية والوضوح والراحة العاطفية.",
        "يحمل كثير من الناس التوتر العاطفي والتوتر الطاقي قبل فترة طويلة من ظهوره جسدياً. مع بدء إطلاق هذا الثقل الطاقي، غالباً ما يصف العملاء شعورهم بالخفة والانفتاح والتوازن العاطفي. كما يلاحظ البعض تخفيف التوتر الجسدي وإحساس أكبر بالراحة والوضوح.",
        "تخلق الجلسة مساحة هادئة وآمنة تسمح للجهاز العصبي بالتباطؤ وللطاقة بإعادة المعايرة بلطف. يُبلغ معظم العملاء عن تحول ملحوظ في حالتهم العاطفية والطاقية مباشرة بعد الجلسة، مع تأثيرات تستمر في التكشف مع مرور الوقت.",
        "صُممت هذه التجربة لتكمل النمو الشخصي والرفاهية العاطفية، وتقدم طريقة مغذية لإعادة الاتصال بنفسك دون ضغط أو استعجال.",
      ],
      benefits: [
        "استعادة التوازن الطاقي",
        "إطلاق التوتر العاطفي",
        "الشعور بالخفة والانفتاح",
        "تخفيف التوتر الجسدي",
        "اكتساب الوضوح والراحة",
        "إعادة معايرة الجهاز العصبي",
      ],
      cta: "احجز جلستك",
    },

    // About
    about: {
      tagline: "تعرفي على مرشدتك",
      headline: "منى بزاري",
      role: "أخصائية علم النفس الإيجابي · متخصصة في الذكاء العاطفي · معالجة أحجار كريمة معتمدة",
      story: [
        "منى بزاري هي أخصائية علم نفس إيجابي ومتخصصة في الذكاء العاطفي ومعالجة أحجار كريمة معتمدة. هي صاحبة مركز شفاء وكهف ملح وتحمل شهادة في علوم الحاسوب من الجامعة الأمريكية بالقاهرة.",
        "يمزج عملها بين العلم والتعاطف والشفاء القائم على الطاقة في نهج متعدد الأبعاد للعافية العاطفية. تخلق منى مساحات يشعر فيها العملاء بالأمان والفهم والدعم العميق.",
        "من خلال الجلسات الفردية وورش العمل والخلوات، تساعد الناس على تطوير الذكاء العاطفي والمرونة والتوازن الداخلي الدائم.",
      ],
      credentials: [
        "ممارسة علم النفس الإيجابي",
        "متخصصة في الذكاء العاطفي",
        "معالجة أحجار كريمة معتمدة",
        "مؤسسة مركز شفاء وكهف ملح",
        "علوم الحاسوب، الجامعة الأمريكية بالقاهرة",
      ],
      cta: "اعمل مع منى",
    },

    // Vision
    vision: {
      tagline: "الفلسفة",
      headline: "الشفاء كأسلوب حياة",
      content: [
        "الشفاء ليس لحظة — إنه أسلوب حياة.",
        "التحول يحدث من خلال الوعي والاستمرارية والتعاطف والمجتمع.",
        "هذه المنصة موجودة لدعم الشفاء كنمط حياة مستدام.",
      ],
      principles: [
        {
          title: "الوعي",
          description: "فهم أنفسنا بعمق وصدق",
        },
        {
          title: "الاستمرارية",
          description: "خطوات صغيرة ومقصودة تتراكم مع الوقت",
        },
        {
          title: "التعاطف",
          description: "صبر لطيف مع أنفسنا والآخرين",
        },
        {
          title: "المجتمع",
          description: "الشفاء معاً في مساحات آمنة وداعمة",
        },
      ],
    },

    // Programs
    programs: {
      tagline: "البرامج والتجارب",
      headline: "رحلة شفائك",
      description: "برامج مصممة بعناية لتلتقي بك حيث أنت وترشدك نحو تحول دائم.",
      items: [
        {
          id: "healthy-tuesday",
          title: "الثلاثاء الصحي",
          arabicTitle: "الثلاثاء الصحي",
          subtitle: "مجتمع شفاء أسبوعي",
          badge: "مجاني",
          description:
            "مجتمع شفاء أسبوعي حضوري يركز على الوعي العاطفي وتخفيف التوتر وتوازن الطاقة والتواصل الهادف.",
          format: "حضوري · أسبوعي",
          focus: [
            "الوعي العاطفي",
            "تخفيف التوتر",
            "توازن الطاقة",
            "التعلم والتواصل",
          ],
          outcomes: [
            "الهدوء والوضوح",
            "الثقة الاجتماعية",
            "الشعور بالانتماء",
            "المرونة العاطفية",
          ],
          cta: "انضمي لمجتمعنا المجاني الآن",
        },
        {
          id: "ensan-farid",
          title: "إنسان فريد",
          arabicTitle: "إنسان فريد",
          subtitle: "كيان فريد",
          badge: "رحلة 3 أشهر",
          description:
            "رحلة تحولية لمدة 3 أشهر للهوية والإتقان العاطفي مصممة لمساعدتك على إعادة الاتصال بذاتك الحقيقية.",
          format: "برنامج مكثف · 3 أشهر",
          focus: [
            "تقدير الذات والهوية",
            "الحدود الصحية",
            "الوضوح العاطفي",
            "العيش بأصالة",
          ],
          outcomes: [
            "ثقة راسخة",
            "استقلال عاطفي",
            "حدود شخصية واضحة",
            "تعبير ذاتي أصيل",
          ],
          investment: {
            monthly: "3,000 جنيه مصري شهرياً",
            full: "9,000 جنيه مصري — دفعة البرنامج الكاملة",
            prepaid: "8,000 جنيه مصري — خصم الدفع المسبق (ادفع مقدماً ووفر)",
          },
          cta: "تقدم الآن",
        },
        {
          id: "youm-el-mona",
          title: "يوم المنى",
          arabicTitle: "يوم المنى",
          subtitle: "يوم الأمنيات",
          badge: "تجربة غامرة",
          description:
            "تجربة شفاء عميقة ليوم واحد مصممة لإعادة الضبط العاطفي والتجديد الطاقي والتوقف الواعي.",
          format: "يوم كامل غامر",
          focus: [
            "إعادة الضبط العاطفي",
            "التجديد الطاقي",
            "التوقف الواعي",
            "التأمل العميق",
          ],
          outcomes: [
            "تجديد طاقي كامل",
            "إطلاق عاطفي",
            "وضوح وسلام",
            "إحساس متجدد بالهدف",
          ],
          investment: {
            base: "1,500 جنيه مصري — تجربة اليوم الواحد",
          },
          cta: "احجز مكانك",
        },
      ],
      consultation: {
        title: "غير متأكدة من أين تبدأين؟",
        description:
          "احجزي مكالمة استكشافية مجانية ولنجد المسار المثالي لرحلة شفائك.",
        cta: "احجزي مكالمة",
      },
    },

    // Testimonials
    testimonials: {
      tagline: "التحولات",
      headline: "قصص الشفاء",
      description: "تجارب حقيقية من نساء سرن في هذا الطريق.",
      items: [
        {
          name: "سارة أ.",
          location: "دبي، الإمارات",
          program: "خريجة إنسان فريد",
          quote:
            "برنامج منى غيّر حياتي. أخيراً أفهم من أنا وماذا أريد. الحدود العاطفية التي بنيتها حوّلت علاقاتي وراحتي النفسية.",
          rating: 5,
        },
        {
          name: "فاطمة ك.",
          location: "القاهرة، مصر",
          program: "منتظمة في الثلاثاء الصحي",
          quote:
            "كل ثلاثاء أصبح نقطة ارتكازي. المجتمع الذي بنته منى مذهل — داعم، بدون أحكام، ويهتم حقاً. لم أشعر أبداً بأنني مفهومة هكذا.",
          rating: 5,
        },
        {
          name: "ليلى م.",
          location: "الرياض، السعودية",
          program: "مشاركة في يوم المنى",
          quote:
            "يوم واحد. هذا كل ما احتجته لتحويل شيء عميق بداخلي. منى تخلق مساحة آمنة جداً لدرجة أنني سمحت لنفسي أخيراً بإطلاق سنوات من المشاعر المكبوتة.",
          rating: 5,
        },
        {
          name: "نور هـ.",
          location: "دبي، الإمارات",
          program: "خريجة إنسان فريد",
          quote:
            "جئت إلى منى وأنا أشعر بالضياع والانفصال. بعد ثلاثة أشهر، أنا امرأة مختلفة تماماً — واثقة، واضحة، وأخيراً في سلام مع نفسي.",
          rating: 5,
        },
        {
          name: "أميرة س.",
          location: "الإسكندرية، مصر",
          program: "جلسات الشفاء بالأحجار الكريمة العلاجية",
          quote:
            "مزيج علم النفس الإيجابي وشفاء الأحجار الكريمة فريد وقوي. نهج منى متجذر في العلم لكنه أيضاً حدسي بعمق.",
          rating: 5,
        },
        {
          name: "هنا ر.",
          location: "دبي، الإمارات",
          program: "برامج متعددة",
          quote:
            "حضرت تقريباً كل ما تقدمه منى. كل تجربة تبني على السابقة. هي حقاً تفهم رحلة الشفاء وتلتقي بكِ تماماً حيث أنتِ.",
          rating: 5,
        },
      ],
    },

    // Gallery
    gallery: {
      tagline: "لحظات",
      headline: "الشفاء في العمل",
      description: "لمحات من برامجنا وخلواتنا وتجمعاتنا المجتمعية.",
    },

    // Connect
    connect: {
      tagline: "لنتواصل",
      headline: "ابدأ رحلتك",
      description:
        "سواء كان لديك أسئلة حول برنامج، أو تريد استكشاف العمل معاً، أو تحتاج ببساطة إلى توجيه — أنا هنا.",
      email: "Mona@monabizzari.com",
      phone: "01006279021",
      workWith: {
        title: "اعمل مع منى",
        items: [
          "البرامج والدورات",
          "الجلسات الخاصة",
          "المحاضرات والفعاليات",
          "العافية المؤسسية",
          "التعاونات والشراكات",
        ],
      },
      form: {
        name: "اسمك",
        email: "البريد الإلكتروني",
        subject: "الموضوع",
        subjectOptions: [
          "البرامج والدورات",
          "الجلسات الخاصة",
          "المحاضرات والفعاليات",
          "التعاونات",
          "استفسار عام",
        ],
        message: "رسالتك",
        submit: "إرسال الرسالة",
        success: "شكراً للتواصل. سأرد خلال 48 ساعة.",
      },
    },

    // Footer
    footer: {
      tagline: "هنا للشفاء — العقل، الطاقة، ونمط الحياة",
      navigation: {
        title: "التنقل",
        items: ["الرسالة", "البرامج", "عني", "قصص النجاح", "تواصل"],
      },
      programs: {
        title: "البرامج",
        items: ["الثلاثاء الصحي", "إنسان فريد", "يوم المنى"],
      },
      connect: {
        title: "تواصل",
        items: ["انستغرام", "فيسبوك"],
      },
      social: {
        instagram: "https://www.instagram.com/bizzarihealingcrystals",
        facebook: "https://www.facebook.com/mona.bizzari",
      },
      copyright: "© 2026 منى بزاري. جميع الحقوق محفوظة.",
      madeWith: "صُنع بحب من UVAII وفريق د. أحمد زلط.",
    },
  },
};

// Premium Photography - Healing & Wellness themed
export const images = {
  hero: {
    main: "https://images.unsplash.com/photo-1607551848581-7ee851bf978b?w=1920&q=80",
    portrait:
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&q=80",
  },
  about: {
    portrait:
      "https://customer-assets.emergentagent.com/job_crystal-therapy/artifacts/laf5qa3d_Mona%20Bizzari%20Section%20photo.jpeg",
    working:
      "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&q=80",
  },
  paths: {
    crystal:
      "https://images.unsplash.com/photo-1515377905703-c4788e51af15?w=600&q=80",
    emotional:
      "https://images.unsplash.com/photo-1518611012118-696072aa579a?w=600&q=80",
    psychology:
      "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600&q=80",
    salt: "https://images.unsplash.com/photo-1600618528240-fb9fc964b853?w=600&q=80",
  },
  programs: {
    healthyTuesday:
      "https://customer-assets.emergentagent.com/job_crystal-therapy/artifacts/yxo3sgur_Healthy%20Tuesday.jpeg",
    ensanFarid:
      "https://customer-assets.emergentagent.com/job_crystal-therapy/artifacts/4astk658_Ensan%20Farid.jpeg",
    youmElMona:
      "https://customer-assets.emergentagent.com/job_crystal-therapy-1/artifacts/z2fkkprc_Youm%20El%20Mona%20Final%20one.jpg",
  },
  crystalHealing:
    "https://customer-assets.emergentagent.com/job_crystal-therapy/artifacts/ouvu6pue_Crystal%20Healing.jpeg",
  book: {
    positivity:
      "https://customer-assets.emergentagent.com/job_crystal-therapy/artifacts/smp8f0oq_Positivity.jpeg",
  },
  gallery: [
    "https://customer-assets.emergentagent.com/job_crystal-therapy/artifacts/ouvu6pue_Crystal%20Healing.jpeg",
    "https://customer-assets.emergentagent.com/job_crystal-therapy/artifacts/4astk658_Ensan%20Farid.jpeg",
    "https://customer-assets.emergentagent.com/job_crystal-therapy/artifacts/yxo3sgur_Healthy%20Tuesday.jpeg",
    "https://customer-assets.emergentagent.com/job_crystal-therapy/artifacts/laf5qa3d_Mona%20Bizzari%20Section%20photo.jpeg",
    "https://customer-assets.emergentagent.com/job_crystal-therapy/artifacts/smp8f0oq_Positivity.jpeg",
    "https://customer-assets.emergentagent.com/job_crystal-therapy-1/artifacts/z2fkkprc_Youm%20El%20Mona%20Final%20one.jpg",
  ],
};
